import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { LMS_SUPPORTED_LANGUAGES } from '../../lookup-definitions/constants/lmsLanguages';
import useTrainingContentOutputData from '../hooks/useTrainingContentOutputData';

const MAX_LENGTH = 1000;

const getFlagClassName = (languageCode) => {
    const codeToFlagSuffixMap = { en: 'us', tr: 'tr', de: 'de', fr: 'fr', it: 'it', ro: 'ro' };
    const suffix = codeToFlagSuffixMap[languageCode];
    return suffix ? `fi fi-${suffix}` : 'fi fi-xx';
};

const blankRows = () => LMS_SUPPORTED_LANGUAGES.map((lang) => ({ languageId: lang.id, trainingContent: '', trainingOutcome: '' }));

/**
 * Eğitim detayındaki "İçerik / Kazanım" sekmesi. Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 1.md §5.
 *
 * Kayıt yoksa (`contentOutput === null`) boş bir 6 dilli form gösterilir ve kaydetme `Add` ucunu
 * çağırır; kayıt varsa aynı form doldurulmuş gösterilir ve kaydetme `Update` ucunu çağırır.
 * Basitlik ve güvenlik için hem ekleme hem güncellemede 6 dilin tamamı gönderilir — API doküman
 * §5.5'e göre bu izinli bir kullanımdır ("Tüm diller ya da yalnızca değişenler gönderilebilir").
 */
const ContentOutputTab = ({ trainingId, readOnly = false }) => {
    const { th } = useLanguage();
    const { contentOutput, isLoading, hasOtherError, isAdding, isUpdating, isDeleting, saveContentOutput, deleteContentOutput } = useTrainingContentOutputData(trainingId);

    const [rows, setRows] = useState(blankRows());
    const [localSubmitted, setLocalSubmitted] = useState(false);

    useEffect(() => {
        if (isLoading) return;
        const backendTranslations = (contentOutput && contentOutput.translations) || [];
        setRows(
            LMS_SUPPORTED_LANGUAGES.map((lang) => {
                const existing = backendTranslations.find((t) => t.languageId === lang.id);
                return {
                    languageId: lang.id,
                    trainingContent: existing?.trainingContent || '',
                    trainingOutcome: existing?.trainingOutcome || ''
                };
            })
        );
        setLocalSubmitted(false);
    }, [contentOutput, isLoading]);

    const handleFieldChange = (languageId, field, value) => {
        setRows((prev) => prev.map((row) => (row.languageId === languageId ? { ...row, [field]: value } : row)));
    };

    const handleSave = async () => {
        setLocalSubmitted(true);
        const missing = rows.some((row) => !row.trainingContent.trim());
        if (missing) return;
        await saveContentOutput(rows.map((row) => ({ languageId: row.languageId, trainingContent: row.trainingContent.trim(), trainingOutcome: (row.trainingOutcome || '').trim() })));
    };

    const handleDelete = () => {
        confirmDialog({
            message: th('trainingCatalog.contentOutput.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => deleteContentOutput()
        });
    };

    if (hasOtherError) {
        return <div className="text-red-500">{th('trainingCatalog.contentOutput.loadError')}</div>;
    }

    return (
        <div>
            <ConfirmDialog />
            {!contentOutput && !isLoading && <p className="text-color-secondary mb-3">{th('trainingCatalog.contentOutput.emptyHint')}</p>}

            <div className="flex flex-column gap-4">
                {LMS_SUPPORTED_LANGUAGES.map((lang) => {
                    const row = rows.find((r) => r.languageId === lang.id) || { trainingContent: '', trainingOutcome: '' };
                    const contentMissing = localSubmitted && !row.trainingContent.trim();

                    return (
                        <div key={lang.id} className="p-3 border-1 border-round" style={{ borderColor: '#E5E7EB' }}>
                            <div className="font-semibold mb-2">
                                <span className={getFlagClassName(lang.code)} style={{ fontSize: '1.2em', marginRight: '0.5em' }} title={lang.name} />
                                {lang.name}
                            </div>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <label className="flex justify-content-between align-items-center">
                                        <span>
                                            {th('trainingCatalog.contentOutput.contentLabel')} <span className="text-red-500">*</span>
                                        </span>
                                        <small className={row.trainingContent.length >= MAX_LENGTH ? 'text-red-500' : 'text-color-secondary'}>
                                            {row.trainingContent.length}/{MAX_LENGTH}
                                        </small>
                                    </label>
                                    <InputTextarea rows={3} autoResize value={row.trainingContent} maxLength={MAX_LENGTH} onChange={(e) => handleFieldChange(lang.id, 'trainingContent', e.target.value)} className={`w-full ${contentMissing ? 'p-invalid' : ''}`} />
                                    {contentMissing && <small className="p-error">{th('trainingCatalog.contentOutput.contentRequired', { language: lang.name })}</small>}
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="flex justify-content-between align-items-center">
                                        <span>{th('trainingCatalog.contentOutput.outcomeLabel')}</span>
                                        <small className={row.trainingOutcome.length >= MAX_LENGTH ? 'text-red-500' : 'text-color-secondary'}>
                                            {row.trainingOutcome.length}/{MAX_LENGTH}
                                        </small>
                                    </label>
                                    <InputTextarea rows={3} autoResize value={row.trainingOutcome} maxLength={MAX_LENGTH} onChange={(e) => handleFieldChange(lang.id, 'trainingOutcome', e.target.value)} className="w-full" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!readOnly && (
                <div className="flex justify-content-between mt-4">
                    <div>{contentOutput && <Button label={th('trainingCatalog.contentOutput.deleteButton')} icon="pi pi-trash" severity="danger" outlined onClick={handleDelete} loading={isDeleting} />}</div>
                    <Button label={th('common.save')} icon="pi pi-check" onClick={handleSave} loading={isAdding || isUpdating} />
                </div>
            )}
        </div>
    );
};

export default ContentOutputTab;
